// socket/VoipDialerListener.tsx
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import useCompany from "../Hooks/company";
import io from 'socket.io-client';
import Api, { getRequestLang } from "../Network";

interface VoipEventData {
  phone?: string;
  ext_name?: string;
  ext_id?: string | number;
  client_id?: number | string;
  event?: string;
  data?: VoipEventData;
  [key: string]: any;
}

interface CustomerData {
  id?: number;
  name?: string;
  phone?: string;
  orders?: any[];
  addresses?: any[];
  activities?: any[];
  most_ordered_products?: any[];
  basket_data?: any;
  [key: string]: any;
}

interface CallInfo {
  name: string;
  clientId: number | string;
  phone: string;
  customer: CustomerData | null;
  timestamp: number;
}

// ─── Step 1: search by phone, find exact match ───────────────────────────────
const searchCustomerByPhone = async (
  phone: string
): Promise<{ id: number; name: string; phone: string } | null> => {
  try {
    const response = await Api.post('customers/search', { keyword: phone });

    let results: any[] = [];
    if (Array.isArray(response.data))            results = response.data;
    else if (Array.isArray(response.data?.data)) results = response.data.data;
    else if (Array.isArray(response))            results = response as any[];

    console.log('[VoipDialerListener] Search results:', results);

    // Exact phone match first, then first result
    const match =
      results.find((c: any) => c.phone === phone || c.mobile === phone) ??
      null;

    console.log('[VoipDialerListener] Exact match:', match);
    return match ?? null;
  } catch (error) {
    console.error('[VoipDialerListener] Search error:', error);
    return null;
  }
};

// ─── Step 2: get full customer details by ID ─────────────────────────────────
const getCustomerById = async (customerId: number): Promise<CustomerData | null> => {
  try {
    const response = await Api.get(`customers/${customerId}/view`);

    // Api returns data directly as response (not response.data)
    const data = response?.data ?? response;

    if (data?.customer) {
      return {
        ...data.customer,
        orders:                data.orders                ?? [],
        addresses:             data.addresses             ?? [],
        activities:            data.activities            ?? [],
        most_ordered_products: data.most_ordered_products ?? [],
        basket_data:           data.basket_data           ?? null,
      };
    }

    if (data?.id) {
      return { orders: [], addresses: [], activities: [], most_ordered_products: [], basket_data: null, ...data };
    }

    console.warn('[VoipDialerListener] Unexpected response shape:', data);
    return null;
  } catch (error) {
    console.error('[VoipDialerListener] Get customer error:', error);
    return null;
  }
};

// ─── Dispatch helpers ─────────────────────────────────────────────────────────
const dispatchVoipEvent = (callInfo: CallInfo) => {
  console.log('[VoipDialerListener] Dispatching event:', callInfo);
  window.dispatchEvent(
    new CustomEvent('voip-incoming-call', {
      detail: { ...callInfo, show: true },
    })
  );
};

type VoipCallPhase = 'call_started' | 'call_answered' | 'call_ended';

const dispatchCallPhase = (
  phase: VoipCallPhase,
  detail: { name: string; clientId: string; phone: string; timestamp: number }
) => {
  console.log(`[VoipDialerListener] Dispatching phase: ${phase}`, detail);
  window.dispatchEvent(new CustomEvent(`voip-${phase}`, { detail }));
};

const SUPPORTED_VOIP_EVENTS = new Set([
  'call_started',
  'call_answered',
  'call_ended',
]);

const apiBaseUrl =
  import.meta.env.VITE_NODE_ENV === 'development'
    ? import.meta.env.VITE_MAPPER_URL
    : import.meta.env.VITE_PROD_MAPPER_URL;

const PERSIST_DEDUPE_MS = 5000;

// Every open tab joins the same `ext_<id>` room, so each one receives the event and would
// POST calls/store. Only the first tab (or first duplicate emit) within the window persists.
// The Web Lock serialises the check-and-set across tabs; without it two tabs could both pass.
const claimVoipEventPersist = async (event: string, extId: string, phone: string): Promise<boolean> => {
  const storageKey = `voip:persisted:${extId}:${event}`;
  const claim = () => {
    try {
      const last = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (last?.phone === phone && Date.now() - Number(last?.at) < PERSIST_DEDUPE_MS) return false;
      localStorage.setItem(storageKey, JSON.stringify({ phone, at: Date.now() }));
    } catch {
      // Storage unavailable: persist rather than drop the event.
    }
    return true;
  };

  const locks = (navigator as any).locks;
  if (!locks?.request) return claim();
  return locks.request(storageKey, claim);
};

const persistVoipEvent = async (
  payload: VoipEventData,
  fallbackVoipId: string,
  userId?: number | string,
  companyId?: number | string,
) => {
  try {
    const event = String(payload?.event || '').toLowerCase();
    if (!SUPPORTED_VOIP_EVENTS.has(event)) return;

    const extId = String(payload?.ext_id ?? fallbackVoipId ?? '').trim();
    const phone = String(payload?.phone ?? '').trim();
    if (!extId || !phone) return;
    if (!(await claimVoipEventPersist(event, extId, phone))) return;

    const requestBody = {
      data: {
        ext_id: extId,
        phone,
        event,
        ext_name: payload?.ext_name ?? null,
        client_id: payload?.client_id ?? null,
        user_id: userId ?? null,
        company_id: companyId ?? null,
      },
    };

    const token = localStorage.getItem('token');
    const endpoints = ['calls/store', 'voip/calls/store'];

    for (const endpoint of endpoints) {
      const response = await fetch(`${apiBaseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json, text/plain, */*',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Lang: getRequestLang(),
          from: 'WEB',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) return;
      if (response.status !== 404) {
        throw new Error(`Failed persist VOIP event (${endpoint}): ${response.status}`);
      }
    }

    console.warn('[VoipDialerListener] VOIP store endpoint not found (404).');
  } catch (error) {
    console.error('[VoipDialerListener] Failed to persist VOIP event:', error);
  }
};

// ─── Main component ───────────────────────────────────────────────────────────
export const VoipDialerListener = () => {
  const { userInfo } = useSelector((state: any) => state.setReducer);
  const { company } = useCompany();
  const socketVoipRef = useRef<any>(null);
  const checkConnectionIntervalRef = useRef<number | undefined>(undefined);

  const companyId =
    company?.id ??
    company?.company_information?.id ??
    company?.companyId;
  const userVoipId = String(
    userInfo?.voip_id ??
    userInfo?.voipId ??
    userInfo?.ext_id ??
    ''
  ).trim();

  const hasCustomerSegmentation = !!(
    company?.main_settings?.models?.some(
      (model: any) => model.title === 'customer_segmentation'
    ) ||
    company?.models?.some(
      (model: any) => model.title === 'customer_segmentation'
    )
  );

  useEffect(() => {
    const VOIP_ENABLED_COMPANY_IDS = [114, 3];
    const shouldInitialize =
      VOIP_ENABLED_COMPANY_IDS.includes(Number(companyId)) || hasCustomerSegmentation;
    console.log('[VoipDialerListener] shouldInitialize:', shouldInitialize);
    console.log('[VoipDialerListener] userInfo?.id:', userInfo?.id);
    console.log('[VoipDialerListener] userVoipId:', userVoipId || '(empty)');
    console.log('[VoipDialerListener] companyId:', companyId);
    console.log('[VoipDialerListener] hasCustomerSegmentation:', hasCustomerSegmentation);
    if (!shouldInitialize || !userInfo?.id || !userVoipId) {
      console.log('[VoipDialerListener] Skipped — conditions not met');
      return;
    }

    console.log('[VoipDialerListener] Initializing socket for user:', userInfo.id);

    const voipSocketUrl =
      import.meta.env.VITE_VOIP_SOCKET_URL || 'https://awfarvoipsocket.awfar.com';

    const socketVoip = io(voipSocketUrl, {
      query:
        'user_id=' + userInfo.id +
        '&page=' + window.location.pathname +
        '&room=ext_' + userVoipId,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      autoConnect: true,
    });

    socketVoipRef.current = socketVoip;

    socketVoip.on('connect', () =>
      console.log('[VoipDialerListener] Socket connected:', socketVoip.id)
    );
    socketVoip.on('disconnect', (reason: string) =>
      console.log('[VoipDialerListener] Socket disconnected:', reason)
    );
    socketVoip.on('connect_error', (error: any) =>
      console.error('[VoipDialerListener] Connection error:', error.message)
    );

    socketVoip.on('socketViop', async (data: VoipEventData) => {
      try {
        console.log('[VoipDialerListener] Incoming call data:', data);

        const payload =
          data?.data && typeof data.data === 'object' ? data.data : data;
        const rawEvent = String(payload?.event || '').toLowerCase();
        const isCallStarted = rawEvent === 'call_started';
        const isCallAnswered = rawEvent === 'call_answered';
        const isCallEnded = rawEvent === 'call_ended';
        const extId = String(payload?.ext_id ?? '').trim();

        // Persist all supported VOIP events to backend.
        await persistVoipEvent(payload, userVoipId, userInfo?.id, companyId);

        if (rawEvent && !isCallStarted && !isCallAnswered && !isCallEnded) {
          console.log('[VoipDialerListener] Ignoring unsupported event:', rawEvent);
          return;
        }

        // Show calls only when event ext_id matches logged-in user's voip_id.
        if (!extId || extId !== userVoipId) {
          console.log('[VoipDialerListener] Ignoring call for different extension:', {
            extId,
            userVoipId,
          });
          return;
        }

        const phone         = payload.phone    ?? '';
        const name          = payload.ext_name ?? phone ?? 'غير معروف';
        const clientId      = payload.client_id != null ? String(payload.client_id) : '';
        const callTimestamp = Date.now();
        const phaseDetail   = { name, clientId, phone, timestamp: callTimestamp };

        if (isCallEnded) {
          dispatchCallPhase('call_ended', phaseDetail);
          return;
        }

        if (isCallAnswered) {
          dispatchCallPhase('call_answered', phaseDetail);
          return;
        }

        // call_started — open drawer in ringing/connecting state
        dispatchCallPhase('call_started', phaseDetail);
        dispatchVoipEvent({ name, clientId, phone, customer: null, timestamp: callTimestamp });

        if (!phone) return;

        // ── Step 1: exact phone search ──
        const matched = await searchCustomerByPhone(phone);
        if (!matched) {
          console.log('[VoipDialerListener] No customer found for phone:', phone);
          return;
        }
        console.log('[VoipDialerListener] Customer matched:', matched);

        // ── Step 2: full details ──
        const fullCustomer = await getCustomerById(matched.id);
        console.log('[VoipDialerListener] Full customer result:', fullCustomer);

        if (fullCustomer) {
          dispatchVoipEvent({
            name:      fullCustomer.name  ?? matched.name ?? name,
            clientId:  String(fullCustomer.id ?? matched.id ?? clientId),
            phone:     fullCustomer.phone ?? matched.phone ?? phone,
            customer:  fullCustomer,
            timestamp: callTimestamp,  // same timestamp → widget merges correctly
          });
        }
      } catch (error) {
        console.error('[VoipDialerListener] Handler error:', error);
      }
    });

    // Keep-alive check every 30 s
    checkConnectionIntervalRef.current = window.setInterval(() => {
      if (socketVoipRef.current && !socketVoipRef.current.connected) {
        console.log('[VoipDialerListener] Reconnecting...');
        socketVoipRef.current.connect();
      }
    }, 30000);

    return () => {
      if (checkConnectionIntervalRef.current)
        window.clearInterval(checkConnectionIntervalRef.current);
      if (socketVoipRef.current) {
        socketVoipRef.current.disconnect();
        socketVoipRef.current = null;
      }
    };
  }, [userInfo?.id, userVoipId, companyId, hasCustomerSegmentation]);

  return null;
};